import { Reward } from 'features/reward/types';
import { isEngineReward } from 'features/reward/utils';

import { MenuObjectTypes } from 'enums/menu';
import { CartEntryType as LegacyCartEntryTypeGateway } from 'generated/graphql-gateway';
import { getOfferNextAvailableDayPart } from 'pages/loyalty/loyalty-offers/utils';
import { t } from 'state/intl/translations';
import {
  LoyaltyOffer,
  LoyaltyReward,
  isLoyaltyOffer,
  isReward,
  isValidOfferIncentive,
} from 'state/loyalty/types';
import { IDayPartBoundary, IValidDayPart } from 'state/menu/main-menu/types';
import {
  IMenuObjectWithDaypart,
  getMenuItemDayParts,
  isAvailableForActiveDayParts,
  timeToString,
} from 'utils/availability';
import { findBurgersForBreakfastDayPart } from 'utils/daypart';
import { routes } from 'utils/routing';

import { IMenuItemDescriptor } from './types';

const REWARD_ID_QUERY_PARAM = 'rewardId';

type DetailIncentive = LoyaltyReward | LoyaltyOffer | Reward;

const isRewardLike = (incentive: DetailIncentive) =>
  isEngineReward(incentive) || isReward(incentive);

const getIncentiveIncentives = (incentive: DetailIncentive) =>
  isEngineReward(incentive)
    ? (incentive.incentives as Array<{ _id?: string; _type?: string } | null> | null) ?? undefined
    : incentive.incentives;

export const makeUrlForIncentive = (
  incentive: DetailIncentive,
  menuItemDescriptor?: IMenuItemDescriptor
) => {
  const descriptor = menuItemDescriptor || getMenuItemDescriptorForIncentive(incentive);
  if (!descriptor) {
    return undefined;
  }

  const queryParam = isLoyaltyOffer(incentive)
    ? ``
    : `?rewardBenefitId=${descriptor._id}&${REWARD_ID_QUERY_PARAM}=${
        isEngineReward(incentive) ? incentive.cmsId ?? incentive.id : incentive._id
      }`;

  return `${routes.menu}/${descriptor._type}/${descriptor._id}${queryParam}`;
};

export interface IMakeIncentiveUnavailableMessageArgs {
  isIncentiveAvailable: boolean;
  incentive: DetailIncentive;
  dayParts: ReadonlyArray<IValidDayPart>;
  isAvailableForDayPart: boolean;
  stagedCartPoints: number;
  pointCost?: number;
  locked?: boolean | null;
  burgersForBreakfastEnabled?: boolean | null;
}

interface ICheckIncentiveAvailableForDayPart {
  incentive: DetailIncentive;
  activeDayParts: IDayPartBoundary[];
  burgersForBreakfastEnabled?: boolean;
}

export const checkIncentiveAvailableForDayPart = ({
  incentive,
  activeDayParts,
  burgersForBreakfastEnabled,
}: ICheckIncentiveAvailableForDayPart) => {
  const burgersForBreakfastDayPart = findBurgersForBreakfastDayPart(activeDayParts);

  if (isLoyaltyOffer(incentive) && incentive.daypart?.length) {
    const dayPartsSet = new Set(incentive.daypart);

    // Burgers For Breakfast
    if (burgersForBreakfastDayPart && dayPartsSet.has(burgersForBreakfastDayPart.key)) {
      return !!burgersForBreakfastEnabled;
    }

    return activeDayParts.some(dayPart => dayPartsSet.has(dayPart.key));
  }

  const incentives = getIncentiveIncentives(incentive);
  if (!incentives?.length) {
    return true;
  }
  // For now get first incentive, future implementation TBD
  const [incentiveItem] = incentives;

  if (incentiveItem?._type === LegacyCartEntryTypeGateway.OFFERDISCOUNT) {
    return true;
  }

  // Burgers For Breakfast
  if (
    burgersForBreakfastDayPart &&
    isAvailableForActiveDayParts({
      activeDayParts: [burgersForBreakfastDayPart],
      menuData: incentiveItem as IMenuObjectWithDaypart,
    })
  ) {
    return !!burgersForBreakfastEnabled;
  }

  const result = isAvailableForActiveDayParts({
    activeDayParts,
    menuData: incentiveItem as IMenuObjectWithDaypart,
  });

  return result;
};

export const makeIncentiveUnavailableMessage = ({
  isIncentiveAvailable,
  dayParts,
  isAvailableForDayPart,
  locked,
  pointCost = 0,
  incentive,
  stagedCartPoints,
  burgersForBreakfastEnabled,
}: IMakeIncentiveUnavailableMessageArgs) => {
  if (locked) {
    return;
  }

  let message: string | undefined = undefined;

  if (!isIncentiveAvailable) {
    message = t('nonAvailableItemInStore');
  } else if (!isAvailableForDayPart) {
    if (isRewardLike(incentive) && getIncentiveIncentives(incentive)?.length) {
      const nextAvailableDayPart = getOfferNextAvailableDayPart(
        dayParts,
        getMenuItemDayParts(getIncentiveIncentives(incentive)?.[0] as IMenuObjectWithDaypart),
        { burgersForBreakfastEnabled }
      );

      message = nextAvailableDayPart
        ? t('nonAvailableRewardUntilX', {
            time: timeToString(nextAvailableDayPart.startTime),
          })
        : t('nonAvailableReward');
    } else if (isLoyaltyOffer(incentive) && incentive.daypart?.length) {
      const nextAvailableDayPart = getOfferNextAvailableDayPart(dayParts, incentive.daypart, {
        burgersForBreakfastEnabled,
      });
      message = nextAvailableDayPart
        ? t('nonAvailableOfferUntil', {
            time: timeToString(nextAvailableDayPart.startTime),
          })
        : t('offerNotAvailable');
    }
  }

  if (!message && stagedCartPoints < pointCost) {
    message = t('exceedsRewardsBalance');
  }

  return message;
};

const findBenefitAvailableInMenu = <IncentiveObject extends readonly ({ _type?: string } | null)[]>(
  incentives: IncentiveObject | null | undefined,
  benefitId?: string
) => {
  return incentives?.find(benefit => {
    // Discounts are not shown in menu
    if (
      !benefit ||
      benefit._type === MenuObjectTypes.OFFER_DISCOUNT ||
      !isValidOfferIncentive(benefit)
    ) {
      return false;
    }
    return benefitId ? benefit?._id === benefitId : true;
  });
};

/**
 * Find first incentive benefit that is available as a menu item.
 * If benefitId is passed, try to find a match.
 */
export const getMenuItemDescriptorForIncentive = (
  incentive: DetailIncentive,
  benefitId?: string
): IMenuItemDescriptor | undefined => {
  const benefitAvailableInMenu = findBenefitAvailableInMenu(
    getIncentiveIncentives(incentive),
    benefitId
  );

  const { _id, _type }: { _id?: string; _type?: string } = benefitAvailableInMenu || {};
  return _id && _type ? { _id, _type } : undefined;
};

export const makeLoyaltyRewardDetailsPath = ({
  rewardId,
  search = '',
}: {
  rewardId: string;
  search?: string;
}) => {
  const rewardIdPath = rewardId ? `/${rewardId}` : '';
  return `${routes.loyaltyRewardsList}${rewardIdPath}${search}`;
};
